import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ClipboardList, Plus, Edit2, Trash2, Package, Calendar, DollarSign, Eye, User, ShoppingBag, ShoppingCart } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { getImageUrl } from '../utils/apiUrl';
const UserFavorites = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fonction pour construire l'URL complète de l'image
  const buildImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    // Utiliser l'URL de l'API depuis l'environnement
    return getImageUrl(imagePath);
  };
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  const [listProducts, setListProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // États pour les formulaires
  const [newList, setNewList] = useState({ name: '', description: '' });
  const [editList, setEditList] = useState({ name: '', description: '' });
  
  // États pour le modal de réservation
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedListForReservation, setSelectedListForReservation] = useState(null);

  useEffect(() => {
    // Vérifier l'authentification
    const token = localStorage.getItem('user_token');
    if (!token) {
      navigate('/login?redirect=' + encodeURIComponent('/account/favorites'));
      return;
    }

    // Charger les informations utilisateur
    apiService.get('/auth/me', { params: { t: Date.now() } })
      .then(res => {
        setUser(res.data?.user || null);
        fetchLists();
      })
      .catch(() => {
        navigate('/login?redirect=' + encodeURIComponent('/account/favorites'));
      });
  }, [navigate]);

  // Nettoyer les timeouts au démontage
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const fetchLists = async () => {
    try {
      const response = await apiService.get('/favorites/lists');
      setLists(response.data.lists);
    } catch (error) {
      console.error('Erreur lors du chargement des listes:', error);
      toast.error('Erreur lors du chargement des listes');
    } finally {
      setLoading(false);
    }
  };

  const fetchListProducts = async (listId) => {
    try {
      const response = await apiService.get(`/favorites/lists/${listId}/products`);
      setListProducts(response.data.products);
      setShowProducts(true);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      toast.error('Erreur lors du chargement des produits');
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.post('/favorites/lists', newList);
      setLists([response.data.list, ...lists]);
      setNewList({ name: '', description: '' });
      setShowCreateModal(false);
      toast.success('Liste créée avec succès');
    } catch (error) {
      console.error('Erreur création liste:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleEditList = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.put(`/favorites/lists/${editingList.id}`, editList);
      setLists(lists.map(list => 
        list.id === editingList.id ? response.data.list : list
      ));
      setShowEditModal(false);
      setEditingList(null);
      toast.success('Liste modifiée avec succès');
    } catch (error) {
      console.error('Erreur modification liste:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette liste ?')) return;
    
    try {
      await apiService.delete(`/favorites/lists/${listId}`);
      setLists(lists.filter(list => list.id !== listId));
      toast.success('Liste supprimée avec succès');
    } catch (error) {
      console.error('Erreur suppression liste:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleRemoveProduct = async (listId, productId) => {
    if (!window.confirm('Retirer ce produit de la liste ?')) return;
    
    try {
      await apiService.delete(`/favorites/lists/${listId}/products/${productId}`);
      setListProducts(listProducts.filter(p => p.product_id !== productId));
      // Mettre à jour le compteur dans la liste
      setLists(lists.map(list => 
        list.id === listId 
          ? { ...list, product_count: list.product_count - 1 }
          : list
      ));
      toast.success('Produit retiré de la liste');
    } catch (error) {
      console.error('Erreur suppression produit:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const openEditModal = (list) => {
    setEditingList(list);
    setEditList({ name: list.name, description: list.description || '' });
    setShowEditModal(true);
  };

  const openProductsModal = (list) => {
    setSelectedList(list);
    fetchListProducts(list.id);
  };

  const searchProducts = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await apiService.getRentmanProducts({
        search: query,
        limit: 10
      });
      setSearchResults(response.data.products || []);
    } catch (error) {
      console.error('Erreur recherche produits:', error);
      toast.error('Erreur lors de la recherche');
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    
    // Annuler le timeout précédent
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Créer un nouveau timeout
    const timeout = setTimeout(() => {
      searchProducts(query);
    }, 300); // Attendre 300ms après la dernière frappe
    
    setSearchTimeout(timeout);
  };

  const addProductToList = async (product) => {
    try {
      await apiService.post(`/favorites/lists/${selectedList.id}/products`, {
        product_id: product.id
      });
      
      // Mettre à jour la liste des produits
      setListProducts([...listProducts, {
        id: Date.now(), // ID temporaire
        product_id: product.id,
        name: product.name,
        description: product.description,
        price_per_day: product.price_per_day,
        image_url: product.image_url,
        added_at: new Date().toISOString()
      }]);
      
      // Mettre à jour le compteur dans la liste
      setLists(lists.map(list => 
        list.id === selectedList.id 
          ? { ...list, product_count: parseInt(list.product_count) + 1 }
          : list
      ));
      
      toast.success('Produit ajouté à la liste');
    } catch (error) {
      console.error('Erreur ajout produit:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  // Fonction pour ouvrir le modal de réservation
  const handleReservationClick = (list) => {
    setSelectedListForReservation(list);
    setShowReservationModal(true);
  };

  // Fonction pour confirmer l'ajout au panier
  const handleConfirmReservation = async () => {
    if (!selectedListForReservation) return;

    try {
      // Récupérer les produits de la liste
      const response = await apiService.get(`/favorites/lists/${selectedListForReservation.id}/products`);
      const products = response.data?.products || [];

      if (products.length === 0) {
        toast.error('Cette liste ne contient aucun produit');
        return;
      }

      // Ajouter chaque produit au panier
      let addedCount = 0;
      for (const product of products) {
        try {
          addToCart({
            id: product.product_id,
            name: product.name,
            price_per_day: product.price_per_day,
            image_url: product.image_url,
            description: product.description
          }, 1); // Quantité par défaut de 1
          addedCount++;
        } catch (error) {
          console.error('Erreur ajout produit au panier:', error);
        }
      }

      if (addedCount > 0) {
        toast.success(`${addedCount} produit(s) de votre liste ajouté(s) au panier`);
        setShowReservationModal(false);
        setSelectedListForReservation(null);
        // Redirection vers le panier
        navigate('/panier');
      } else {
        toast.error('Aucun produit n\'a pu être ajouté au panier');
      }
    } catch (error) {
      console.error('Erreur récupération produits:', error);
      toast.error('Erreur lors de la récupération des produits');
    }
  };

  // Fonction pour annuler la réservation
  const handleCancelReservation = () => {
    setShowReservationModal(false);
    setSelectedListForReservation(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de vos listes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ClipboardList className="h-8 w-8 text-orange-500 mr-3" />
                Mes Listes
              </h1>
              <p className="mt-2 text-gray-600">
                Organisez vos listes de matériel et utilisez-les pour passer des réservations
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Nouvelle Liste
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation gauche */}
          <div className="lg:col-span-1">
            <div className="card">
              <nav className="space-y-2">
                <Link 
                  to="/account"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm text-gray-700 hover:bg-gray-50"
                >
                  <User className="h-4 w-4 mr-2" />
                  Mon compte
                </Link>
                <Link 
                  to="/account/reservations"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm text-gray-700 hover:bg-gray-50"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Mes réservations
                </Link>
                <Link 
                  to="/account/favorites"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm bg-orange-50 text-orange-600 border border-orange-100 shadow-sm"
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Mes listes
                </Link>
              </nav>
            </div>
          </div>

          {/* Contenu droite */}
          <div className="lg:col-span-3">

        {/* Liste des listes */}
        {lists.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune liste</h3>
            <p className="text-gray-600 mb-6">Créez votre première liste pour organiser vos produits</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Créer ma première liste
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {lists.map((list) => (
              <div key={list.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{list.name}</h3>
                    {list.description && (
                      <p className="text-sm text-gray-600 mb-2">{list.description}</p>
                    )}
                    <div className="flex items-center text-sm text-gray-500">
                      <Package className="h-4 w-4 mr-1" />
                      {list.product_count} produit{list.product_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(list)}
                      className="p-2 text-gray-400 hover:text-orange-500 transition-colors"
                      title="Modifier"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteList(list.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => openProductsModal(list)}
                    className="btn-secondary flex items-center text-sm"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Voir les produits
                  </button>
                  
                  {list.product_count > 0 && (
                    <button
                      onClick={() => handleReservationClick(list)}
                      className="btn-primary flex items-center text-sm"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Réserver
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de création de liste */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Créer une nouvelle liste</h3>
              <form onSubmit={handleCreateList}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la liste *
                  </label>
                  <input
                    type="text"
                    value={newList.name}
                    onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                    className="input-field"
                    placeholder="Ex: Matériel photo"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optionnel)
                  </label>
                  <textarea
                    value={newList.description}
                    onChange={(e) => setNewList({ ...newList, description: e.target.value })}
                    className="input-field"
                    rows="3"
                    placeholder="Description de votre liste..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary"
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn-primary">
                    Créer la liste
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de modification de liste */}
        {showEditModal && editingList && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Modifier la liste</h3>
              <form onSubmit={handleEditList}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la liste *
                  </label>
                  <input
                    type="text"
                    value={editList.name}
                    onChange={(e) => setEditList({ ...editList, name: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optionnel)
                  </label>
                  <textarea
                    value={editList.description}
                    onChange={(e) => setEditList({ ...editList, description: e.target.value })}
                    className="input-field"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="btn-secondary"
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn-primary">
                    Sauvegarder
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal des produits d'une liste */}
        {showProducts && selectedList && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedList.name} ({listProducts.length} produit{listProducts.length !== 1 ? 's' : ''})
                </h3>
                <button
                  onClick={() => setShowProducts(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              {/* Barre de recherche */}
              <div className="mb-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Rechercher un produit à ajouter..."
                    className="flex-1 input-field"
                  />
                  <button
                    onClick={() => setShowSearch(!showSearch)}
                    className="btn-secondary"
                  >
                    {showSearch ? 'Masquer' : 'Rechercher'}
                  </button>
                </div>
                
                {/* Résultats de recherche */}
                {showSearch && searchQuery && (
                  <div className="mt-4 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                    {searchResults.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Aucun produit trouvé
                      </div>
                    ) : (
                      <div className="space-y-2 p-2">
                        {searchResults.map((product) => (
                          <div key={product.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                            <div className="flex items-center space-x-3">
                              {product.image_url ? (
                                <img
                                  src={buildImageUrl(product.image_url)}
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                  <Package className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <h4 className="font-medium text-gray-900">{product.name}</h4>
                              </div>
                            </div>
                            <button
                              onClick={() => addProductToList(product)}
                              className="btn-primary text-sm"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Ajouter
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {listProducts.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">Cette liste est vide</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(
                      listProducts.reduce((acc, product) => {
                        const category = product.category_name || 'Sans catégorie';
                        if (!acc[category]) {
                          acc[category] = [];
                        }
                        acc[category].push(product);
                        return acc;
                      }, {})
                    ).sort(([a], [b]) => {
                      // Trier par ordre alphabétique, "Sans catégorie" en dernier
                      if (a === 'Sans catégorie') return 1;
                      if (b === 'Sans catégorie') return -1;
                      return a.localeCompare(b, 'fr');
                    }).map(([category, products]) => (
                      <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <h3 className="font-semibold text-gray-900">{category}</h3>
                          <p className="text-sm text-gray-600">{products.length} produit{products.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre du produit</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Supprimer</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {products.map((product) => (
                                <tr key={product.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    {product.image_url ? (
                                      <img
                                        src={buildImageUrl(product.image_url)}
                                        alt={product.name}
                                        className="w-16 h-16 object-cover rounded"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div 
                                      className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center"
                                      style={{ display: product.image_url ? 'none' : 'flex' }}
                                    >
                                      <Package className="h-8 w-8 text-gray-400" />
                                    </div>
                                  </td>
                                  <td className="px-4 py-4">
                                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                    {product.code && (
                                      <div className="text-sm text-gray-500">Code: {product.code}</div>
                                    )}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      onClick={() => handleRemoveProduct(selectedList.id, product.product_id)}
                                      className="text-red-400 hover:text-red-600 p-2"
                                      title="Retirer de la liste"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmation de réservation */}
        {showReservationModal && selectedListForReservation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <ShoppingCart className="h-6 w-6 text-orange-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Faire une demande de réservation
                </h3>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Voulez-vous ajouter tous les produits de la liste <strong>"{selectedListForReservation.name}"</strong> à votre panier pour faire une demande de réservation ?
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Package className="h-4 w-4 mr-2" />
                    <span>{selectedListForReservation.product_count} produit{selectedListForReservation.product_count !== 1 ? 's' : ''} sera{selectedListForReservation.product_count !== 1 ? 'ient' : ''} ajouté{selectedListForReservation.product_count !== 1 ? 's' : ''} au panier</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelReservation}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmReservation}
                  className="btn-primary flex items-center"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserFavorites;
